import React, {ReactComponentElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useQueryClient} from '@tanstack/react-query';
import { useAwardManualStar, useGetCoachRecognitionPanel, useGetCoachZonePanel, useGetStudentRecognitionState, useComputeEndCelebration, useApproveEndCelebration, useGetEndCelebrationDisplay, useClassroomGameWon } from './recognition/api/useRecognitionApi';
import { refreshLeaderboardSoon, refreshRecognitionSoon } from './recognition/api/refreshSoon';
import { CleanCoachDrawer } from './recognition/CleanCoachDrawer';
import { OverlayManager, OverlayState } from './recognition/overlays';
import { StarFlyHtml } from './recognition/overlays/htmlOverlays';
import { ThinStudentStrip, ThinCoachStrip } from './recognition/RecognitionStrip';
import { AwardToastHtml } from './recognition/AwardToastHtml';
import { EndOfClassConfirmModal } from './recognition/EndOfClassConfirmModal';
import { StudentMiniBanner, StudentStarsScreen } from './recognition/StudentMobile';
import { useGetStudentStarEvents } from './recognition/api/useRecognitionApi';
import type { CelebrationData } from './recognition/types';
import {View, Text, TouchableOpacity, StyleSheet} from 'react-native-web';
import ENV from '../../../environment';
import IframeZoom from './components/Zoom/IframeZoom';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import Divider from '@/Atoms/Divider/Divider';
import {Dimensions, Modal, ScrollView} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import webinarStore from './store/zoomInfoStore';
import Loader from '@/Atoms/CCLoader/CCLoader';
import {ScreenName} from '@/navigators/StackConstants';
import ActivePuzzle from './components/ActivePuzzle/ActivePuzzle';
import {
  markPuzzlePush,
  setPuzzleTelemetryContext,
  trackPuzzle,
  trackPuzzleError,
} from './utils/puzzleTelemetry';
import useClassroomInfoApi from '@/Hooks/ClassroomInfoApi/useClassroomInfoApi';
import ComingSoon from '../ComingSoon';
import {useSocket, useSocketReconnect} from '@/Hooks/UseSocket';
import CompletedPuzzle from './components/CompletedPuzzle/CompletedPuzzle';
import useGetActivePuzzleApi from '@/Hooks/ClassroomInfoApi/useGetActivePuzzleApi';
import Leaderboard from './components/Leaderboard/Leaderboard';
import ClassroomActivityPanel from './components/Leaderboard/ClassroomActivityPanel';
import Summary from './components/Summary/Summary';
import {FontSizes} from '@/Utils/Dimensions';
import CCButton from '@/Atoms/CCButton';
import CustomVideoPlayer from '@/Atoms/VideoPlayerWeb';
import {useVideoCourseByIdApi} from '@/Hooks/BooksApi/useBooksApi';
import ClassroomNotification from './utils/ClassroomNotification/ClassroomNotification';

import useClassroomStore from './store/timerStore';
import Box from '@/Atoms/Box/Box';
import {RFPercentage} from 'react-native-responsive-fontsize';
import useSound from 'use-sound';
import notificationSoundMp3 from '../../Assets/Sounds/single_class_alert.mp3';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import useNudgeApi from '@/Hooks/ClassroomInfoApi/useNudgeApi';
import useCreateMiniTournamentApi, {
  CreateMiniTournamentResponse,
} from '@/Hooks/ClassroomInfoApi/useCreateMiniTournamentApi';
import useGetActiveMiniTournamentApi, {
  ActiveMiniTournament,
} from '@/Hooks/ClassroomInfoApi/useGetActiveMiniTournamentApi';
import useStartMiniTournamentNowApi, {
  StartMiniTournamentNowResponse,
} from '@/Hooks/ClassroomInfoApi/useStartMiniTournamentNowApi';
import useJoinMiniTournamentApi, {
  JoinMiniTournamentResponse,
} from '@/Hooks/ClassroomInfoApi/useJoinMiniTournamentApi';
import useGetMiniTournamentPlayerCountApi, {
  GetMiniTournamentPlayerCountResponse,
} from '@/Hooks/ClassroomInfoApi/useGetMiniTournamentPlayerCountApi';
import MiniTournamentCreateModal from './components/MiniTournamentCreateModal';
import MiniTournamentBanner from './components/MiniTournamentBanner';
import ClassroomTestBanner from './components/ClassroomTestBanner';
import MiniTournamentStartConfirmModal from './components/MiniTournamentStartConfirmModal';
import RoyaleCreateModal from './components/RoyaleCreateModal';
import PuzzleRoyaleBanner from './components/PuzzleRoyaleBanner';
import IconTooltipButton from './components/IconTooltipButton';
import useGetActiveClassroomRoyaleApi, {
  ActiveClassroomRoyale,
} from '@/Hooks/ClassroomInfoApi/useGetActiveClassroomRoyaleApi';
import useCreateClassroomRoyaleApi from '@/Hooks/ClassroomInfoApi/useCreateClassroomRoyaleApi';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import {
  armRoyaleRoomCreatedResolver,
  clearRoyaleRoomCreatedResolver,
} from '@/Hooks/FunChallengesSocket/royaleSocketRouting';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';
import useGamePlayState from '@/Store/GamePlayStore';
import SlideModal from '@/Atoms/SlideModal';
import {PopUp} from '../Visualisation/components/LevelModal';
import ClassReviewModal from '../Books/components/ClassReviewModal';
import HelpModal from '@/Components/HelpModal';
import {CLASSROOM_HELP_SECTIONS} from './utils/helpContent';
import ClassEndCountdown from './components/ClassEndCountdown/ClassEndCountdown';

// Memoised at the use site (not inside each component's own file) so the
// WS/poll-driven re-renders of the Classroom root don't cascade into every
// tab. See spec CO-6.
const MemoIframeZoom = React.memo(IframeZoom);
const MemoCustomVideoPlayer = React.memo(CustomVideoPlayer);
const MemoCompletedPuzzle = React.memo(CompletedPuzzle);
const MemoActivePuzzle = React.memo(ActivePuzzle);
const MemoClassroomActivityPanel = React.memo(ClassroomActivityPanel);
const MemoSummary = React.memo(Summary);
const EMPTY_LIST: any[] = [];

const Classroom = ({dashboardData}) => {
  const {height: screenHeight} = Dimensions.get('window');
  const [recognitionStripHeight, setRecognitionStripHeight] = useState(75);
  const dynamicHeight = screenHeight - 120 - recognitionStripHeight;
  const {first_name, last_name, username, mobile_number} =
    dashboardData?.user || {};
  //It removes all the digits in the name
  const userName =
    [first_name, last_name]
      ?.map(v => (typeof v === 'string' ? v?.replace(/\d/g, '')?.trim() : ''))
      ?.join(' ')
      ?.trim() || 'Guest';

  // Initialize webinarData state
  const [webinarData, setWebinarData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigation = useNavigation();
  const [source, setSource] = useState('live');
  const [selectedVideo, setSelectedVideo] = useState({});
  const [message, setMessage] = useState('');
  const [disable, setDisable] = useState(false);
  const [nudgeMessage, setNudgeMessage] = useState('');
  const [icon, setIcon] = useState({});
  const [showNudgePopup, setShowNudgePopup] = useState(false);
  const getVideoCourseByIdSuccess = data => {
    console.log('video data', data);
    setSelectedVideo(data?.data);
    setLoading(false);
  };
  const [showPopup, setShowPopup] = useState(false);
  const [holdLabel, setHoldLabel] = useState('');
  const [showClassReviewModal, setShowClassReviewModal] = useState(false);
  const [classReviewMeta, setClassReviewMeta] = useState({
    webinarId: 0,
    studentId: 0,
    topic: 'Demo Class',
    coachName: 'Coach',
    classType: 'default',
  });
  const ICON_MAP = {
    1: {
      name: 'check',
      bgColor: CCColors.Green,
      iconColor: CCColors.CCTableBg,
    },
    2: {
      name: 'information-variant',
      bgColor: CCColors.Level.Yellow.iconColor,
      iconColor: CCColors.White,
    },
    3: {
      name: 'close',
      bgColor: CCColors.WrongMoves.Background,
      iconColor: CCColors.White,
    },
  };
  const getVideoCourseByIdError = error => {};
  const duration = useClassroomStore(s => s.duration);
  const timerDuration = useClassroomStore(s => s.timerDuration);
  const initiateTimer = useClassroomStore(s => s.initiateTimer);
  const {mutate: getVideoCourseById} = useVideoCourseByIdApi(
    getVideoCourseByIdSuccess,
    getVideoCourseByIdError,
  );
  const [showTimer, setShowTimer] = useState(false);
  const [hasPlayedSound, setHasPlayedSound] = useState(false);
  const [notificationSound] = useSound(notificationSoundMp3);
  const timerStartedRef = useRef(false);
  const [countdownExpiry, setCountdownExpiry] = useState<Date | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [lowTimeReached, setLowTimeReached] = useState(false);
  const handleCountdownExpire = useCallback(() => {
    console.log('Timer expired');
    setShowTimer(false);
    setIsRunning(false);
  }, []);
  const handleCountdownLowTime = useCallback(() => {
    setLowTimeReached(true);
  }, []);
  // Delay before starting timer
  useEffect(() => {
    if (["mentor_call", "ptm"].includes(batchType)) return;
    if (duration === null || timerDuration === null || !initiateTimer) return;
    if (timerStartedRef.current) return;

    const timeoutId = setTimeout(() => {
      console.log('Starting countdown timer');
      timerStartedRef.current = true;
      setShowTimer(true);
      const soundKey = meetingId && occurenceId ? `classEndSound_${meetingId}_${occurenceId}` : null;
      const alreadyPlayed = soundKey ? !!sessionStorage.getItem(soundKey) : false;
      setHasPlayedSound(alreadyPlayed);

      const newExpiry = new Date();
      newExpiry.setSeconds(newExpiry.getSeconds() + timerDuration);
      setCountdownExpiry(newExpiry);
      setIsRunning(true);
    }, duration * 1000);

    return () => clearTimeout(timeoutId);
  }, [duration, timerDuration, initiateTimer, batchType]);

  // Load data from localStorage on component mount
  //  Local change
  useEffect(() => {
    const loadWebinarData = () => {
      try {
        const storedData = localStorage.getItem('ZoomUserData');
        console.log('Raw localStorage data:', storedData);

        // const storedData = {
        //   token:
        //     'WGkyamrvBkRU6DO58zWKnn4GXPjXnnZoCX7BIqkoS4Y.DQgAAAAVVupAJhZVZDBnTU44c1EwR2h5UFVFNHktVF9RAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
        //   meetingId: '92364693075',
        //   password: '818945',
        //   occurenceId: '1753828200001',
        //   meetingRole: first_name === 'Haricharann' ? 'host' : 'student',
        //   isZoomMeeting: 1,
        //   batchType: 'Foundation2',
        //   topic: 'X-Ray Attack',
        // };

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          console.log('Parsed webinar data:', parsedData);
          setWebinarData(parsedData);
          setSource(parsedData.source);
          if (parsedData.source == 'recording') {
            getVideoCourseById({courseId: parsedData.videoId});
          }
          // setWebinarData(storedData);
        } else {
          console.error('No webinarData found in localStorage. Redirecting...');
          navigation.navigate(ScreenName.Books, {tab: 'meeting'});
        }
      } catch (error) {
        console.error('Error parsing webinarData from localStorage:', error);
        navigation.navigate(ScreenName.Books, {tab: 'meeting'});
      } finally {
        setDataLoaded(true);
      }
    };

    loadWebinarData();
  }, []); // Empty dependency array - runs only once on mount

  // Extract data from webinarData once it's loaded

  const {
    token,
    meetingId,
    password,
    occurenceId,
    meetingRole,
    isZoomMeeting,
    batchType: rawBatchType,
    classType,
    callType,
    webinarRowId,
    studentId,
    topic,
    isTestClass,
    joinUrl,
  } = webinarData || {};

  // ── TEMP TEST OVERRIDE — Demo-Call flow on a normal class ────────────────────
  const DEMO_TEST_MEETING_ID = '';//'94579370184'; // TODO: set to IND-F2-169's meetingId to enable
  const batchType =
    DEMO_TEST_MEETING_ID && String(meetingId) === DEMO_TEST_MEETING_ID
      ? 'Demo Call'
      : rawBatchType;
  // const meetingRole = 'host';
  const {schema, cc_ws_client} = require('../../../CCClientSdk');

  useEffect(() => {
    if (isRunning && !hasPlayedSound) {
      notificationSound();
      setHasPlayedSound(true);
      const soundKey = meetingId && occurenceId ? `classEndSound_${meetingId}_${occurenceId}` : null;
      if (soundKey) sessionStorage.setItem(soundKey, 'true');
    }
  }, [isRunning, meetingRole, hasPlayedSound, notificationSound]);

  const [activeTabLeft, setActiveTabLeft] = useState<number>(0);
  const [activeTabRight, setActiveTabRight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [isExitingFullscreen, setIsExitingFullscreen] = useState(false);

  const userEmail =
    isZoomMeeting === 1
      ? dashboardData?.user?.email
      : meetingRole === 'host'
      ? 'support@circlechess.com'
      : dashboardData?.user?.email;
  const userId: string = dashboardData?.user?.user_id;
  // Read live inside the once-registered WS handlers (they close over first-render values).
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const [currentActivePuzzle, setCurrentActivePuzzle] = useState({
    puzzleId: '',
    puzzleName: '',
    totalMoveCount: '',
    startingFEN: '',
    moveList: [] as any[],
    orientation: '',
    puzzleTime: '',
    createdAt: '',
    remainingTime: '',
  });
  const [pastPuzzleId, setPastPuzzleId] = useState<number>(0);
  const [testParams, setTestParams] = useState<{bookKey: string; name: string} | null>(null);
  const [testBannerDismissed, setTestBannerDismissed] = useState<boolean>(false);
  const [solvedActivePuzzle, setSolvedActivePuzzle] = useState<boolean>(false);
  const [notificationState, setNotificationState] = useState({
    data: null,
    isVisible: false,
  });
  const [manualAwardCount, setManualAwardCount] = useState(0);
  const [overlayState, setOverlayState] = useState<OverlayState | null>(null);
  const overlayQueueRef = React.useRef<OverlayState[]>([]);
  const overlayKeyRef = React.useRef<number>(0);
  const [overlayKey, setOverlayKey] = useState<number>(0);
  const [starFlyState, setStarFlyState] = useState<{count: number; key: number} | null>(null);
  const starFlyKeyRef = React.useRef<number>(0);
  const lastOverlayAtRef = React.useRef<number>(0);
  const showOverlay = useCallback((kind: OverlayState['kind'], props?: Record<string, any>) => {
    lastOverlayAtRef.current = Date.now();
    const next: OverlayState = {kind, props};
    setOverlayState(curr => {
      if (curr?.kind) {
        overlayQueueRef.current.push(next);
        return curr;
      }
      overlayKeyRef.current += 1;
      setOverlayKey(overlayKeyRef.current);
      return next;
    });
  }, []);
  const clearOverlay = useCallback(() => {
    const next = overlayQueueRef.current.shift();
    if (next) {
      overlayKeyRef.current += 1;
      setOverlayKey(overlayKeyRef.current);
    }
    setOverlayState(next ?? null);
  }, []);
  const fireStarFly = useCallback((count: number) => {
    starFlyKeyRef.current += 1;
    setStarFlyState({count: Math.max(1, count), key: starFlyKeyRef.current});
  }, []);
  const clearStarFly = useCallback(() => setStarFlyState(null), []);
  const prevStarsRef = React.useRef<number | null>(null);

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    (window as any).__showOverlay = showOverlay;
    (window as any).__fireStarFly = fireStarFly;
  }

  // Initialize zoomMeetingInfo only after webinarData is loaded
  const [zoomMeetingInfo, setZoomMeetingInfo] = useState<{
    meetingId: string;
    tk: string;
    pwd: string;
    signature: string;
    userName: string;
    userEmail: string;
  }>({
    meetingId: '',
    tk: '',
    pwd: '',
    signature: '',
    userName: '',
    userEmail: '',
  });

  // Update zoomMeetingInfo when webinarData is available
  useEffect(() => {
    if (webinarData && dataLoaded) {
      console.log('Updating zoomMeetingInfo with webinarData:', webinarData);
      setZoomMeetingInfo({
        meetingId: `${meetingId || ''}`,
        tk: `${token || ''}`,
        pwd: `${password || ''}`,
        signature: '',
        userName: `${userName}`,
        userEmail: `${userEmail || ''}`,
      });
    }
  }, [
    webinarData,
    dataLoaded,
    meetingId,
    token,
    password,
    userName,
    userEmail,
  ]);

  const tabsLeft = [
    {
      key: 'Coach Screen',
      label: 'Coach Screen',
      icon: 'monitor',
      component: source == 'recording' ? MemoCustomVideoPlayer : MemoIframeZoom,
      getProps: () =>
        source == 'recording'
          ? {
              selectedVideo,
              setCurrentActivePuzzle,
            }
          : {
              zoomMeetingInfo,
              meetingRole,
              isZoomMeeting,
              classType,
              callType,
              webinarRowId,
              studentId,
              topic,
              coachPanelStudents: coachPanelData?.students ?? EMPTY_LIST,
              coachManualCount: manualAwardCount,
              coachMysteryCount: coachPanelData?.mysteryCount ?? 0,
              onAward: isCoach ? handleAward : undefined,
            },
    },
    {
      key: 'Past Puzzles',
      label: 'Past Puzzles',
      icon: 'check-circle-outline',
      component: MemoCompletedPuzzle,
      getProps: () => ({
        meetingRole,
        meetingId,
        occurenceId,
        activeTabLeft,
        source,
      }),
      uniqueKey: pastPuzzleId || 'no-past-puzzle',
    },
    {
      key: 'Active Puzzle',
      label: 'Active Puzzle',
      icon: 'puzzle-outline',
      component: MemoActivePuzzle,
      getProps: () => ({
        meetingId,
        occurenceId,
        meetingRole,
        topic,
        currentActivePuzzle,
        isActiveTab: activeTabLeft === 2 ? true : false,
        setActiveTabLeft,
        setSolvedActivePuzzle,
        source,
        isTestClass,
        testBookId: testParams?.bookKey,
        testBookName: testParams?.name,
        setNudgeMessage,
        setShowNudgePopup,
        mysteryCount: coachPanelData?.mysteryCount ?? 0,
        onAutoStarsAwarded: handleAutoStarsAwarded,
        onMysteryActivated: handleMysteryActivated,
        onMysteryRejected: handleMysteryRejected,
      }),
      uniqueKey: currentActivePuzzle?.puzzleId || 'no-puzzle',
    },
  ];

  const tabsRight = [
    // {
    //   key: 'Chat Room',
    //   label: 'Chat Room',
    //   icon: 'chat',
    //   component: () => <Text>Coming soon.</Text>,
    //   getProps: () => ({
    //     // dashboardData,
    //   }),
    // },
    {
      key: 'Leaderboard',
      label: 'Leaderboard',
      icon: 'chart-box-outline',
      component: MemoClassroomActivityPanel,
      getProps: () => ({
        meetingRole,
        meetingId,
        occurenceId,
        source,
        currentActivePuzzle,
        activeTabRight,
        onAward: isCoach ? handleAward : undefined,
        classStudents: coachPanelData?.students ?? EMPTY_LIST,
        currentUserId: userId,
        // per-student zone (+ reason/action) -> row highlight + dot + click-to-see-detail
        zoneByUserId,
      }),
    },
    {
      key: 'Summary',
      label: 'Summary',
      icon: 'note-text-outline',
      component: MemoSummary,
      getProps: () => ({
        meetingId,
        occurenceId,
        currentActivePuzzle,
        meetingRole,
        solvedActivePuzzle,
        activeTabRight,
        source,
      }),
      uniqueKey: currentActivePuzzle?.puzzleId || 'no-puzzle',
    },
  ];

  const socket = useSocket();

  const handleExitFullScreen = () => {
    console.log('Exit Full Screen 1');

    const isFullscreen = !!(
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    );

    if (!isFullscreen) {
      console.log('Not in fullscreen mode');
      return;
    }

    console.log('Exit Full Screen 2 - Document is in fullscreen');

    const exitFullscreen = async () => {
      try {
        if (document.exitFullscreen) {
          console.log('Exit Full Screen 3 - Using exitFullscreen');
          await document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          console.log('Exit Full Screen 3 - Using mozCancelFullScreen');
          await document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          console.log('Exit Full Screen 3 - Using webkitExitFullscreen');
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          console.log('Exit Full Screen 3 - Using msExitFullscreen');
          await document.msExitFullscreen();
        }
        console.log('Exit Full Screen 4 - Successfully exited fullscreen');
      } catch (error) {
        console.error('Error while exiting fullscreen:', error);
        trackPuzzleError('classroom_puzzle_fullscreen_exit_failed', error);

        if (error.message?.includes('Document not active')) {
          console.log('Attempting to focus window and retry...');
          try {
            window.focus();
            setTimeout(async () => {
              try {
                if (document.exitFullscreen) {
                  await document.exitFullscreen();
                } else if (document.mozCancelFullScreen) {
                  await document.mozCancelFullScreen();
                } else if (document.webkitExitFullscreen) {
                  await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                  await document.msExitFullscreen();
                }
                console.log('Successfully exited fullscreen after refocus');
              } catch (retryError) {
                console.error(
                  'Failed to exit fullscreen even after refocus:',
                  retryError,
                );
              }
            }, 100);
          } catch (focusError) {
            console.error('Failed to focus window:', focusError);
          }
        }
      }
    };

    if (!document.hasFocus()) {
      console.log('Document does not have focus, attempting to focus...');
      window.focus();
      setTimeout(() => {
        exitFullscreen();
      }, 100);
    } else {
      console.log('Document has focus, proceeding with exit fullscreen');
      exitFullscreen();
    }
  };

  useEffect(() => {
    if (
      dataLoaded &&
      meetingRole !== 'student' &&
      localStorage.getItem('meetingStarted')
    ) {
      console.log('Meeting role inside leave', meetingRole);
      const handleBeforeUnload = event => {
        // setCloseModal(true);
        console.log('beforeunload event triggered');
        event.returnValue = 'Are you sure you want to leave?';
        return 'Are you sure you want to leave?';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [dataLoaded]);

  const checkActivePuzzle = (webSocket, wsData) => {
    console.log('WebSocket works? ', webSocket, 'Data received', wsData);

    const hasMeetingInfo = !!(
      zoomMeetingInfo.signature &&
      zoomMeetingInfo.meetingId &&
      zoomMeetingInfo.tk &&
      zoomMeetingInfo.pwd
    );
    markPuzzlePush({
      will_fetch: hasMeetingInfo,
      has_signature: !!zoomMeetingInfo.signature,
      has_meeting_id: !!zoomMeetingInfo.meetingId,
      has_tk: !!zoomMeetingInfo.tk,
      has_pwd: !!zoomMeetingInfo.pwd,
      has_occurence_id: !!occurenceId,
      was_fullscreen: !!document.fullscreenElement,
      exit_fullscreen_skipped: isExitingFullscreen,
    });

    if (!isExitingFullscreen) {
      setIsExitingFullscreen(true);
      const exitStartedAt = Date.now();
      handleExitFullScreen();
      // Sync portion only; the exit itself is async inside handleExitFullScreen.
      trackPuzzle('classroom_puzzle_fullscreen_exit_called', {
        sync_ms: Date.now() - exitStartedAt,
      });
      setTimeout(() => setIsExitingFullscreen(false), 500);
    }
    if (hasMeetingInfo) {
      getActivePuzzle({
        meetingId: zoomMeetingInfo.meetingId,
        occurenceId,
        trigger: 'ws_push',
      });
    }
    setActiveTabLeft(2);
    setActiveTabRight(0);
    setSolvedActivePuzzle(false);
  };

  const handleOnSuccessClassroomInfo = data => {
    console.log('Signature received?', data);
    setZoomMeetingInfo(prev => ({...prev, signature: data?.signature}));
    setDisable(data?.disable ?? false);
  };

  const handleOnSuccessGetActivePuzzle = data => {
    console.log('handleOnSuccessGetActivePuzzle data?', data);
    setCurrentActivePuzzle({
      puzzleId: data?.active_puzzle?.puzzle_id,
      puzzleName: data?.active_puzzle?.puzzle_name,
      totalMoveCount: data?.active_puzzle?.total_move_count,
      startingFEN: data?.active_puzzle?.starting_fen,
      moveList: data?.active_puzzle?.move_list ?? [],
      orientation: data?.active_puzzle?.orientation,
      puzzleTime: data?.active_puzzle?.puzzle_time,
      createdAt: data?.active_puzzle?.created_at,
      remainingTime: data?.active_puzzle?.remaining_time,
    });

    const bookId = data?.class_test_publish?.book_id;
    const bookName = data?.class_test_publish?.book_name ?? "";

    if (bookId) {
      const cleanBookKey = String(bookId).replace(/[^0-9]/g, '');
      setTestParams({bookKey: cleanBookKey, name: bookName});
      setTestBannerDismissed(false);
    }
  };
  const handleOnErrorClassroomInfo = error => {
    setMessage(error?.response?.data?.error);
    setHoldLabel(error?.response?.data?.hold_status_label ?? '');
    setShowPopup(true);
  };
  const {mutate: getClassroomInfo} = useClassroomInfoApi(
    handleOnSuccessClassroomInfo,
    handleOnErrorClassroomInfo,
  );

  const handleOnSuccessNudge = data => {
    console.log(data, 'data');
    const isSuccess = data?.status === 'success';

    setDisable(data?.disable ?? false);

    setIcon(isSuccess ? ICON_MAP?.[data?.icon] ?? ICON_MAP[2] : ICON_MAP[3]);

    setNudgeMessage(data?.message ?? '');
    setShowNudgePopup(true);
  };

  const handleOnErrorNudge = data => {
    setDisable(false);
    if (data?.message) {
      setShowNudgePopup(true);
      setNudgeMessage(data?.message);
      setIcon(ICON_MAP[3]);
    }
  };
  const {mutate: nudge} = useNudgeApi(handleOnSuccessNudge, handleOnErrorNudge);

  // ---- Recognition Panel / Celebration Logic ----
  // Coach UI = anyone who isn't a student (host, panelist, mentor, …). They get
  // the full recognition coach experience (strip, panel, alerts, awards, wrap-up).
  // Meeting-host-only actions (end meeting, tournaments, nudge) keep their own
  // explicit `meetingRole === 'host'` checks below, so non-host coaches don't end
  // the meeting or start tournaments.
  const isCoach = meetingRole !== 'student';
  const queryClient = useQueryClient();

  useEffect(() => {
    setPuzzleTelemetryContext({
      userId,
      meetingId: meetingId ? `${meetingId}` : undefined,
      occurenceId: occurenceId ? `${occurenceId}` : undefined,
      role: isCoach ? 'coach' : 'student',
    });
  }, [userId, meetingId, occurenceId, isCoach]);

  const { data: coachPanelData } = useGetCoachRecognitionPanel(
    meetingId || '',
    occurenceId || '',
    { enabled: isCoach && !!meetingId && !!occurenceId, refetchInterval: 10000 },
  );

  const { data: zonePanelData } = useGetCoachZonePanel(
    meetingId || '',
    occurenceId || '',
    meetingRole || '',
    // zones are a slow-moving signal -> poll every 2 min (very light on the server)
    { enabled: isCoach && !!meetingId && !!occurenceId, refetchInterval: 120000 },
  );

  const { data: studentRecognitionState } = useGetStudentRecognitionState(
    meetingId || '',
    occurenceId || '',
    { enabled: !isCoach && !!meetingId && !!occurenceId, refetchInterval: 30000 },
  );

  useEffect(() => {
    if (isCoach) return;
    const current = studentRecognitionState?.stars;
    if (current == null) return;
    const prev = prevStarsRef.current;
    if (prev != null && current > prev) {
      fireStarFly(current - prev);
    }
    prevStarsRef.current = current;
  }, [studentRecognitionState?.stars, isCoach, fireStarFly]);

  const [pendingAwardCoach, setPendingAwardCoach] = useState<{studentName: string; awardType: string} | null>(null);

  const [wrapUpVisible, setWrapUpVisible] = useState(false);
  const [computedCelebration, setComputedCelebration] = useState<CelebrationData | null>(null);
  const [celebrationTrigger, setCelebrationTrigger] = useState(0);
  const [starsScreenVisible, setStarsScreenVisible] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(Dimensions.get('window').width);

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({window}) => {
      setViewportWidth(window.width);
    });
    return () => sub?.remove?.();
  }, []);
  const isMobile = viewportWidth < 768;

  const { data: studentStarEventsData } = useGetStudentStarEvents(
    meetingId || '',
    occurenceId || '',
    { enabled: !isCoach && isMobile && starsScreenVisible && !!meetingId && !!occurenceId },
  );

  const { mutate: computeEndCelebration } = useComputeEndCelebration((data) => {
    if (data?.celebration) {
      setComputedCelebration(data.celebration);
      setWrapUpVisible(true);
    }
  });
  const { mutate: approveEndCelebration } = useApproveEndCelebration(() => {
    setWrapUpVisible(false);
  });

  const { data: endCelebrationDisplay, refetch: refetchEndCelebration } = useGetEndCelebrationDisplay(
    meetingId || '',
    occurenceId || '',
    { enabled: !isCoach && !!meetingId && !!occurenceId && celebrationTrigger > 0 },
  );

  useEffect(() => {
    if (isCoach) return;
    if (celebrationTrigger === 0) return;
    refetchEndCelebration();
  }, [celebrationTrigger, isCoach, refetchEndCelebration]);

  useEffect(() => {
    if (isCoach) return;
    const c = endCelebrationDisplay?.celebration;
    if (!c) return;
    const coachStarName = (() => {
      const id = (c as any).coach_star_student;
      if (!id) return undefined;
      const found = (c as any).students?.find((s: any) => s.user_id === id);
      return found?.name;
    })();
    showOverlay('celebration', {
      awards: (c as any).awards ?? [],
      allEarned: (c as any).all_earned_stars ?? false,
      coachStarStudentName: coachStarName,
    });
  }, [endCelebrationDisplay?.celebration, isCoach, showOverlay]);

  const handleOpenWrapUp = useCallback(() => {
    if (!meetingId || !occurenceId) return;
    computeEndCelebration({ meetingId, occurenceId });
  }, [meetingId, occurenceId, computeEndCelebration]);

  // auto-show wrap-up 2 minutes before class ends
  const autoWrapUpFiredRef = useRef(false);
  useEffect(() => {
    if (!isCoach || !isRunning || !lowTimeReached || wrapUpVisible || autoWrapUpFiredRef.current) return;
    autoWrapUpFiredRef.current = true;
    handleOpenWrapUp();
  }, [isCoach, isRunning, lowTimeReached, wrapUpVisible, handleOpenWrapUp]);

  const handlePublishCelebration = useCallback((payload: {awardsData: CelebrationData; autoAwardAssignments: Record<string, number>; extraStars: {studentId: number; count: number}[]; extraAwards: {studentId: number; awardType: string; count: number}[]}) => {
    if (!meetingId || !occurenceId) return;
    approveEndCelebration({
      meetingId,
      occurenceId,
      awardsData: payload.awardsData,
      autoAwardAssignments: payload.autoAwardAssignments,
      extraStars: payload.extraStars,
      extraAwards: payload.extraAwards,
    });
  }, [meetingId, occurenceId, approveEndCelebration]);

  const pendingAwardStudentIdRef = useRef<number | null>(null);
  const { mutate: reportGameWon } = useClassroomGameWon();

  const { mutate: awardManualStar } = useAwardManualStar(
    (data) => {
      setManualAwardCount(c => c + 1);
      if (pendingAwardCoach) {
        showOverlay('award_toast', {
          studentName: pendingAwardCoach.studentName,
          awardKey: pendingAwardCoach.awardType,
        });
        setPendingAwardCoach(null);
      }
      const sid = pendingAwardStudentIdRef.current;
      const pendingType = pendingAwardCoach?.awardType;
      if (sid != null) {
        queryClient.setQueryData<any>(
          ['coachRecognitionPanel', meetingId || '', occurenceId || ''],
          (old: any) => {
            if (!old?.students) return old;
            return {
              ...old,
              students: old.students.map((s: any) => {
                if (Number(s.user_id) !== sid) return s;
                const prevCounts = s.manual_award_counts || {};
                return {
                  ...s,
                  stars: (s.stars ?? 0) + 1,
                  coach_awards: (s.coach_awards ?? 0) + 1,
                  manual_award_counts: pendingType
                    ? {...prevCounts, [pendingType]: (prevCounts[pendingType] ?? 0) + 1}
                    : prevCounts,
                };
              }),
            };
          },
        );
        pendingAwardStudentIdRef.current = null;
      }
    },
    (error: any) => {
      const msg = error?.response?.data?.error || 'Award failed';
      console.error('[award_manual_star]', msg, error);
      setPendingAwardCoach(null);
      pendingAwardStudentIdRef.current = null;
    },
  );

  const handleAward = useCallback((studentId: number, awardType: string) => {
    if (!meetingId || !occurenceId) return;
    const student = coachPanelData?.students?.find(s => String(s.user_id) === String(studentId));
    const studentName = student?.name || `Student ${studentId}`;
    setPendingAwardCoach({studentName, awardType});
    pendingAwardStudentIdRef.current = Number(studentId);
    awardManualStar({
      meetingId,
      occurenceId,
      studentUserId: studentId,
      awardType: awardType as any,
      meetingRole,
    });
  }, [meetingId, occurenceId, meetingRole, awardManualStar, coachPanelData, showOverlay]);

  // Stable identity so MemoActivePuzzle doesn't re-render every root render.
  const handleAutoStarsAwarded = useCallback((verifyData: any) => {
    // Fire star fly immediately based on response (don't wait for 5s poll).
    if (!isCoach) {
      const newTotal = typeof verifyData?.stars === 'number' ? verifyData.stars : null;
      const prev = prevStarsRef.current ?? 0;
      if (newTotal != null && newTotal > prev) {
        fireStarFly(newTotal - prev);
        prevStarsRef.current = newTotal;
      }
    }
    if (verifyData?.speed_flash) {
      const sf = verifyData.speed_flash;
      showOverlay('speed_flash', {
        name: isCoach ? 'Student' : (userName || 'You'),
        solveTime: sf.solve_time != null ? `${sf.solve_time}s` : '',
      });
    }
    if (verifyData?.streak?.new_tier && verifyData?.streak?.current) {
      showOverlay('streak', { streak: verifyData.streak.current });
    }
    if (verifyData?.mystery_bonus_claimed) {
      showOverlay('award_toast', {
        studentName: isCoach ? 'Student' : 'You',
        awardKey: 'mystery_bonus',
      });
    }
  }, [isCoach, fireStarFly, showOverlay, userName]);

  const handleMysteryActivated = useCallback(() => {
    showOverlay('mystery', {});
  }, [showOverlay]);

  const handleMysteryRejected = useCallback(() => {
    console.log('Mystery rejected: limit hit');
  }, []);

  // per-student zone (+ reason/action) -> row highlight + dot + click-to-see-detail
  const zoneByUserId = useMemo(
    () =>
      Object.fromEntries(
        (zonePanelData?.students ?? []).map((s: any) => [
          String(s.user_id),
          {zone: s.zone, reasons: s.zone_reasons ?? [], action: s.action ?? ''},
        ]),
      ),
    [zonePanelData?.students],
  );

  // ---- Mini Tournament logic ----

  const [miniTournamentResult, setMiniTournamentResult] =
    useState<CreateMiniTournamentResponse | null>(null);
  const handleMiniTournamentSuccess = (data: CreateMiniTournamentResponse) => {
    setMiniTournamentResult(data);
    setIsCreateMiniModalOpen(false);
    setShowNudgePopup(true);
    if (data?.success) {
      const notified = data?.students_notified ?? 0;
      setNudgeMessage(
        `Mini tournament created. ${notified} student(s) notified — they'll see an Open Tournament prompt in the classroom.`,
      );
      setIcon(ICON_MAP[1]);
      if (data.tournament_id && data.name && data.start_date && data.end_date) {
        setActiveMiniTournament({
          tournament_id: data.tournament_id,
          name: data.name,
          start_date: data.start_date,
          end_date: data.end_date,
          created_by: 0,
        });
      }
    } else {
      setNudgeMessage(data?.error || 'Failed to create mini tournament.');
      setIcon(ICON_MAP[3]);
    }
  };
  const handleMiniTournamentError = () => {
    setShowNudgePopup(true);
    setNudgeMessage('Failed to create mini tournament. Please try again.');
    setIcon(ICON_MAP[3]);
  };
  const {
    mutate: createMiniTournament,
    isLoading: isCreatingMiniTournament,
  } = useCreateMiniTournamentApi(
    handleMiniTournamentSuccess,
    handleMiniTournamentError,
  );

  const [isCreateMiniModalOpen, setIsCreateMiniModalOpen] = useState(false);
  const [activeMiniTournament, setActiveMiniTournament] =
    useState<ActiveMiniTournament | null>(null);

  const handleGetActiveMiniSuccess = (data: any) => {
    if (data?.success && data?.tournament) {
      setActiveMiniTournament(data.tournament);
    }
  };
  const {mutate: getActiveMiniTournament} = useGetActiveMiniTournamentApi(
    handleGetActiveMiniSuccess,
  );

  const handleStartNowSuccess = (data: StartMiniTournamentNowResponse) => {
    setShowNudgePopup(true);
    if (data?.success) {
      if (data.status === 'imminent') {
        setNudgeMessage('Tournament is starting any moment now.');
        setIcon(ICON_MAP[2]);
      } else {
        setNudgeMessage('Tournament started.');
        setIcon(ICON_MAP[1]);
        if (data.effective_start_date && activeMiniTournament) {
          setActiveMiniTournament({
            ...activeMiniTournament,
            start_date: data.effective_start_date,
          });
        }
      }
    } else {
      setNudgeMessage(data?.error || 'Failed to start tournament now.');
      setIcon(ICON_MAP[3]);
    }
  };
  const handleStartNowError = () => {
    setShowNudgePopup(true);
    setNudgeMessage('Failed to start tournament now. Please try again.');
    setIcon(ICON_MAP[3]);
  };
  const {mutate: startMiniTournamentNow, isLoading: isStartingNow} =
    useStartMiniTournamentNowApi(handleStartNowSuccess, handleStartNowError);

  const joinedTournamentId = useGamePlayState(s => s.joinedTournamentId);
  const openTournamentInNewTab = (tournamentId: number, name?: string) => {
    if (typeof window === 'undefined' || !tournamentId) return;
    const params = new URLSearchParams();
    params.set('tournamentId', String(tournamentId));
    if (name) params.set('tournamentName', name.replace(/\s/g, '-'));
    const url = `${window.location.origin}/playChess?${params.toString()}`;
    window.open(url, '_blank');
  };

  // Student "Open Tournament": join once, then open the tournament tab.
  // hasJoinedActiveTournament avoids a redundant join API call on repeat
  // clicks (the server treats re-join as success anyway, but skipping the
  // round-trip is cleaner).
  const [hasJoinedActiveTournament, setHasJoinedActiveTournament] =
    useState(false);
  const handleJoinSuccessForOpen = (data: JoinMiniTournamentResponse) => {
    if (data?.success && activeMiniTournament) {
      setHasJoinedActiveTournament(true);
      openTournamentInNewTab(
        activeMiniTournament.tournament_id,
        activeMiniTournament.name,
      );
    } else {
      setShowNudgePopup(true);
      setNudgeMessage(data?.error || 'Could not join the tournament.');
      setIcon(ICON_MAP[3]);
    }
  };
  const {mutate: joinMiniTournament, isLoading: isJoiningTournament} =
    useJoinMiniTournamentApi(handleJoinSuccessForOpen, () => {
      setShowNudgePopup(true);
      setNudgeMessage('Could not join the tournament. Please try again.');
      setIcon(ICON_MAP[3]);
    });
  const handleStudentOpenTournament = () => {
    if (!activeMiniTournament || isJoiningTournament) return;
    if (hasJoinedActiveTournament) {
      openTournamentInNewTab(
        activeMiniTournament.tournament_id,
        activeMiniTournament.name,
      );
      return;
    }
    joinMiniTournament({tournament_id: activeMiniTournament.tournament_id});
  };

  // ---- Classroom Puzzle Royale logic ----
  // A coach can create MANY royales over a class (unlike the single mini
  // tournament). We track only the latest active one for the student Join
  // banner. The lobby itself lives in cc-game-service; the coach client creates
  // it in a NEW tab (via the FunChallenges lobby auto-create handoff), which
  // also POSTs to Django to persist + notify students.
  const [isCreateRoyaleModalOpen, setIsCreateRoyaleModalOpen] = useState(false);
  const [activeRoyale, setActiveRoyale] = useState<ActiveClassroomRoyale | null>(
    null,
  );
  const [isJoiningRoyale, setIsJoiningRoyale] = useState(false);
  // Student dismissed the Join banner. Reset whenever a different royale arrives
  // (keyed on room_id) so a freshly-created royale re-shows the banner.
  const [royaleBannerDismissed, setRoyaleBannerDismissed] = useState(false);
  // The room_id we've already auto-opened for THIS student, so the auto-open
  // fires once per royale (not on every 20s activeRoyale refresh).
  const autoOpenedRoyaleRef = useRef<string | null>(null);

  const handleGetActiveRoyaleSuccess = (data: any) => {
    if (data?.success) {
      const next: ActiveClassroomRoyale | null = data.royale ?? null;
      setActiveRoyale(prev => {
        if (next?.room_id && next.room_id !== prev?.room_id) {
          // A new royale → un-dismiss the banner.
          setRoyaleBannerDismissed(false);
        }
        return next;
      });
    }
  };
  const {mutate: getActiveClassroomRoyale} = useGetActiveClassroomRoyaleApi(
    handleGetActiveRoyaleSuccess,
  );

  const openRoyaleLobbyTab = (params: URLSearchParams) => {
    if (typeof window === 'undefined') return;
    const url = `${window.location.origin}/FunChallengesLobby?${params.toString()}`;
    window.open(url, '_blank');
  };

  // Is there a classroom royale still active (not yet expired, no game finished
  // yet)? Only ONE may be active per class at a time. The button below flips
  // between "Create" (none active) and "Open" (one active); it reverts to
  // "Create" after the game ends (server stamps ended_at → get_active returns
  // null → activeRoyale clears on the 20s refresh).
  const hasActiveRoyale = !!(
    activeRoyale && new Date(activeRoyale.expires_at).getTime() > Date.now()
  );

  // Open the host into an EXISTING active royale (rejoin as host via its share
  // code; the server recognises creatorId → restores host + Start button).
  const reopenActiveRoyaleAsHost = (shareCode?: string) => {
    const code = shareCode || activeRoyale?.share_code;
    if (!code) return;
    const params = new URLSearchParams();
    params.set('mode', 'multiplayer');
    params.set('join', code);
    params.set('autoJoin', '1');
    // Carry classroom context so the battle tab restores classroomContext and
    // can register a REMATCH lobby with Django (notify students). Without it a
    // rematch from the coach's reopened tab wouldn't get a classroom row.
    if (meetingId) params.set('crMeeting', meetingId);
    if (occurenceId) params.set('crOccurence', occurenceId);
    openRoyaleLobbyTab(params);
  };

  // ── Instant create (no navigation) ──────────────────────────────────────
  // The coach creates the lobby RIGHT HERE in the warm classroom tab (its
  // socket is already authenticated), so it's instant — no new-tab cold-load.
  // createLobby is fire-and-forget; the server's fun_room_created lands the
  // roomId/shareCode in the royale store (the global listener routes it). We
  // watch for that, then: POST to Django (persist row + notify students),
  // refresh activeRoyale (flips the button to "Open"), and RESET the royale
  // store so the classroom tab isn't left as a phantom lobby participant. The
  // lobby keeps living in the game-service (Redis) for its 15-min TTL with
  // hostId=coach, so the coach later rejoins-as-host via "Open Puzzle Royale"
  // and students join by code — both independent of this tab.
  const {createLobby: royaleCreateLobby, authed: royaleSocketAuthed} =
    useRoyaleSocket();
  // Recreates THIS tab's game-service socket. cc-conn-mgr routes every
  // server→client message by userId to a SINGLE "active" socket (latest
  // connection wins). When the coach opens a royale battle tab, that tab becomes
  // the active socket; after it closes, the classroom tab is left WITHOUT an
  // active socket mapping ("No single socket ID found"), so a later royale's
  // fun_room_created — routed by userId — is emitted to a dead/empty sid and
  // dropped. The classroom create then hangs until the 10s timeout and reverts
  // to "Create" (the coach's reported "2nd royale does nothing"). Reconnecting
  // re-runs auth → the server swaps the active sid back to THIS tab, so the ACK
  // comes home. The coach later reopening the battle tab swaps it away again —
  // which is fine, the create is already finalized by then.
  const reconnectSocket = useSocketReconnect();
  const {mutate: registerClassroomRoyale} = useCreateClassroomRoyaleApi();
  const pendingRoyaleCreateRef = useRef(false);
  // Holds the settings for a create that is WAITING for the post-reclaim
  // reconnect to re-authenticate. The auth-watch effect below fires the actual
  // fun_room_create once the fresh socket is authed.
  const pendingReconnectCreateRef = useRef<RoyaleSettingsValue | null>(null);
  // Drives the button-disabled state during the create→register→refresh window
  // (the icon only flips to "Open" once activeRoyale refreshes, a couple
  // seconds later). Without this the coach could fire a 2nd create meanwhile.
  const [creatingRoyale, setCreatingRoyale] = useState(false);
  // Subscribe to the store lobby so this effect fires only when it CHANGES
  // (i.e. when fun_room_created lands), not on every render.
  const createdRoyaleLobby = useFunChallengesRoyaleStore(s => s.lobby);

  // Finalize a freshly-created classroom royale: persist to Django (notify
  // students), flip the button to "Open", reset the local royale store, refresh
  // the authoritative active row. Idempotent on roomId via a ref so the resolver
  // path and the store-watch fallback can't double-fire for the same room.
  const finalizedRoyaleRoomRef = useRef<string | null>(null);
  const finalizeRoyaleCreate = useCallback(
    (info: {
      roomId: string;
      shareCode: string;
      expiresAt: number;
      settings?: RoyaleSettingsValue;
    }) => {
      console.log('[royale-dbg] finalizeRoyaleCreate called', {
        roomId: info.roomId,
        alreadyFinalized: finalizedRoyaleRoomRef.current === info.roomId,
      });
      if (info.roomId.indexOf('fun_room_') !== 0) return; // never a share code
      if (finalizedRoyaleRoomRef.current === info.roomId) return; // already done
      finalizedRoyaleRoomRef.current = info.roomId;
      pendingRoyaleCreateRef.current = false;
      // Reset so the classroom tab stops being a phantom lobby participant; the
      // room persists server-side for its TTL.
      useFunChallengesRoyaleStore.getState().reset();
      // Optimistically flip the button straight to "Open Puzzle Royale" (no
      // enabled "Create" gap). The refresh below confirms with the server copy.
      setActiveRoyale({
        room_id: info.roomId,
        share_code: info.shareCode,
        expires_at: new Date(info.expiresAt).toISOString(),
        created_by: 0,
      });
      setCreatingRoyale(false);
      if (meetingId && occurenceId) {
        // CRITICAL ORDERING: the refresh MUST run only AFTER the create POST has
        // committed the row. Previously both fired in the same tick, so the
        // refetch usually resolved BEFORE the create committed and read the PRE-
        // create state (null after a finished royale, or the stale prior one),
        // which then overwrote the optimistic setActiveRoyale above → the button
        // snapped back to "Create" and the coach saw "nothing happened" — even
        // with a single tab open. Chaining the refresh onto the create's success
        // makes the confirming read see the freshly-created row.
        registerClassroomRoyale(
          {
            meetingId,
            occurenceId,
            roomId: info.roomId,
            shareCode: info.shareCode,
            settings: info.settings,
            expiresAt: info.expiresAt,
          },
          {
            onSettled: () => {
              getActiveClassroomRoyale({meetingId, occurenceId});
            },
          },
        );
      }
    },
    [meetingId, occurenceId, registerClassroomRoyale, getActiveClassroomRoyale],
  );

  // Disarm the one-shot resolver if the coach leaves the classroom mid-create.
  useEffect(() => {
    return () => clearRoyaleRoomCreatedResolver();
  }, []);

  // FALLBACK only: if the one-shot resolver (armed in handleCoachCreateRoyale)
  // somehow didn't fire but the store DID receive the new room on this tab,
  // finalize from the store snapshot. The resolver is the primary path because
  // the store can hold a STALE room on a 2nd create (the fun_room_created ACK
  // landed on the host's battle tab under single-sid routing), which is what
  // made the 2nd create re-POST the old id → Django duplicate → no new row.
  useEffect(() => {
    if (!pendingRoyaleCreateRef.current) return;
    if (!createdRoyaleLobby?.roomId || !createdRoyaleLobby?.shareCode) return;
    if (createdRoyaleLobby.roomId.indexOf('fun_room_') !== 0) return;
    finalizeRoyaleCreate({
      roomId: createdRoyaleLobby.roomId,
      shareCode: createdRoyaleLobby.shareCode,
      expiresAt: createdRoyaleLobby.expiresAt,
      settings: createdRoyaleLobby.settings,
    });
  }, [createdRoyaleLobby, finalizeRoyaleCreate]);

  // Coach pressed the icon. If one is active, OPEN it (rejoin as host in a new
  // tab). Otherwise open the settings modal to CREATE.
  const handleCoachCreateRoyalePressed = () => {
    if (creatingRoyale) return; // a create is in flight — block a 2nd one
    if (hasActiveRoyale) {
      reopenActiveRoyaleAsHost();
      return;
    }
    setIsCreateRoyaleModalOpen(true);
  };

  // Actually arm the resolver + send fun_room_create. Called only once the
  // classroom tab's socket is the ACTIVE one (post-reclaim reconnect + re-auth),
  // so the server's fun_room_created ACK — routed by userId to the active sid —
  // comes back to THIS tab and fires the resolver.
  const sendRoyaleCreate = useCallback(
    (settings: RoyaleSettingsValue) => {
      console.log('[royale-dbg] sendRoyaleCreate — sending fun_room_create (socket reclaimed + authed)');
      pendingRoyaleCreateRef.current = true;
      finalizedRoyaleRoomRef.current = null; // allow THIS create to finalize
      // PRIMARY path: arm the one-shot resolver so the EXACT room from this
      // create's fun_room_created ACK is what we register — independent of the
      // store snapshot (which can lag/hold a stale room on a 2nd create). The
      // resolver carries roomId/shareCode/expiresAt; settings come from here.
      armRoyaleRoomCreatedResolver(info => {
        finalizeRoyaleCreate({...info, settings});
      });
      // Safety: if fun_room_created never lands, don't leave the button disabled
      // forever — re-enable so the coach can tap again, and disarm the resolver.
      setTimeout(() => {
        if (pendingRoyaleCreateRef.current) {
          console.log('[royale-dbg] 10s TIMEOUT fired — fun_room_created never reached this tab/resolver. Re-enabling Create.');
          pendingRoyaleCreateRef.current = false;
          clearRoyaleRoomCreatedResolver();
          setCreatingRoyale(false);
        }
      }, 10000);
      royaleCreateLobby({
        hostIsPlayer: true,
        // isClassroom marks this as a classroom royale so the backend excludes
        // the COACH (host) from the puzzle dedupe pool — coaches have solved a
        // huge number of puzzles and would otherwise strip fresh puzzles from
        // the students. Standalone royales never set this flag (host stays a
        // normal deduped player). See funPuzzleRoyale.generateSequence +
        // fun_challenges puzzle_sequence coach_id.
        settings: {...settings, isClassroom: true},
        creatorName: userName || username || '',
        creatorAvatarUrl: undefined,
      });
    },
    [royaleCreateLobby, finalizeRoyaleCreate, userName, username],
  );

  // Once the post-reclaim reconnect has RE-AUTHENTICATED this tab's socket, fire
  // the create that was waiting. royaleSocketAuthed flips false on reconnect()
  // and true again on auth_complete — that true-transition is our cue.
  useEffect(() => {
    if (!royaleSocketAuthed) return;
    const settings = pendingReconnectCreateRef.current;
    if (!settings) return;
    pendingReconnectCreateRef.current = null;
    console.log('[royale-dbg] socket re-authed after reclaim — firing pending create');
    sendRoyaleCreate(settings);
  }, [royaleSocketAuthed, sendRoyaleCreate]);

  // Coach confirmed settings. RECLAIM this tab's socket first (see
  // reconnectSocket above): reconnect → the server makes this tab the active
  // socket again → the fun_room_created ACK routes home. We can't send the
  // create until the fresh socket re-authenticates, so stash the settings and
  // let the auth-watch effect above fire it on auth_complete. If the socket is
  // somehow already authed and active we'd still reconnect — but that's
  // harmless: by the time a coach creates a royale, the prior battle tab is
  // closed, so there's no live game to disrupt by swapping the active sid here.
  const handleCoachCreateRoyale = (settings: RoyaleSettingsValue) => {
    setIsCreateRoyaleModalOpen(false);
    if (!meetingId || !occurenceId) return;
    console.log('[royale-dbg] handleCoachCreateRoyale FIRED — reclaiming socket via reconnect');
    setCreatingRoyale(true);
    pendingReconnectCreateRef.current = settings;
    reconnectSocket();
    // Belt-and-suspenders: if re-auth never completes, don't leave the button
    // stuck disabled with a dangling pending create.
    setTimeout(() => {
      if (pendingReconnectCreateRef.current === settings) {
        console.log('[royale-dbg] reconnect/re-auth TIMEOUT — never re-authed, re-enabling Create');
        pendingReconnectCreateRef.current = null;
        setCreatingRoyale(false);
      }
    }, 10000);
  };

  // Student: open the latest royale by its share code in a new tab and join
  // AUTOMATICALLY (autoJoin=1) so the student lands straight in the battle — no
  // second click on a Join button. The join is handled entirely by the royale
  // socket once the tab loads; no Django round-trip here.
  const handleStudentJoinRoyale = () => {
    if (!activeRoyale?.share_code || isJoiningRoyale) return;
    setIsJoiningRoyale(true);
    const params = new URLSearchParams();
    params.set('mode', 'multiplayer');
    params.set('join', activeRoyale.share_code);
    params.set('autoJoin', '1');
    openRoyaleLobbyTab(params);
    // Re-enable shortly; opening a tab is fire-and-forget.
    setTimeout(() => setIsJoiningRoyale(false), 1500);
  };

  // STUDENT auto-open: when a coach creates a royale, the student gets the
  // classroom_puzzle_royale_created WS message → getActiveClassroomRoyale →
  // activeRoyale is set (which also raises the Join banner). At that moment we
  // ALSO auto-open the royale in a new tab (web) so the student lands straight
  // in it — no manual Join click. Fires ONCE per royale (autoOpenedRoyaleRef
  // keyed on room_id) and ONLY for students (the coach opens it himself). If a
  // browser popup-blocks the WS-initiated window.open, the Join banner is still
  // there as the fallback. (App does the equivalent via stack navigation.)
  useEffect(() => {
    if (meetingRole !== 'student') return;
    const roomId = activeRoyale?.room_id;
    if (!roomId || !activeRoyale?.share_code) return;
    if (autoOpenedRoyaleRef.current === roomId) return; // already auto-opened
    autoOpenedRoyaleRef.current = roomId;
    handleStudentJoinRoyale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoyale?.room_id, activeRoyale?.share_code, meetingRole]);

  const handleStudentOpenTest = () => {
    if (!testParams) return;
    const url = `${window.location.origin}/resources?tab=user_assignment&name=${encodeURIComponent(
      testParams?.name ?? '',
    )}&bookKey=${testParams?.bookKey}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Coach "Start Now": check player parity first; prompt if odd.
  const [oddPlayerModalVisible, setOddPlayerModalVisible] = useState(false);
  const triggerStartNow = (joinCoach: boolean) => {
    if (!activeMiniTournament) return;
    setOddPlayerModalVisible(false);
    startMiniTournamentNow({
      meetingId,
      occurenceId,
      tournament_id: activeMiniTournament.tournament_id,
      join_coach: joinCoach,
    });
  };
  const handlePlayerCountSuccess = (
    data: GetMiniTournamentPlayerCountResponse,
  ) => {
    if (!data?.success) {
      setShowNudgePopup(true);
      setNudgeMessage(data?.error || 'Could not fetch player count.');
      setIcon(ICON_MAP[3]);
      return;
    }
    if (data.is_odd) {
      setOddPlayerModalVisible(true);
    } else {
      triggerStartNow(false);
    }
  };
  const {mutate: getMiniTournamentPlayerCount, isLoading: isFetchingPlayerCount} =
    useGetMiniTournamentPlayerCountApi(handlePlayerCountSuccess, () => {
      setShowNudgePopup(true);
      setNudgeMessage('Could not fetch player count. Please try again.');
      setIcon(ICON_MAP[3]);
    });
  const handleCoachStartNow = () => {
    if (!activeMiniTournament || isStartingNow || isFetchingPlayerCount) return;
    getMiniTournamentPlayerCount({
      tournament_id: activeMiniTournament.tournament_id,
    });
  };

  const {mutate: getActivePuzzle} = useGetActivePuzzleApi(
    handleOnSuccessGetActivePuzzle,
  );

  const handleClassroomNotification = wsData => {
    try {
      let classroomNotificationParsed = wsData.message;
      if (typeof classroomNotificationParsed === 'string') {
        classroomNotificationParsed = JSON.parse(classroomNotificationParsed);
      }

      if (typeof classroomNotificationParsed === 'string') {
        classroomNotificationParsed = JSON.parse(classroomNotificationParsed);
      }
      console.log(
        'Received classroom notification:',
        classroomNotificationParsed,
      );
      setNotificationState({
        data: classroomNotificationParsed,
        isVisible: true,
      });
    } catch (error) {
      console.error('Error parsing classroom notification:', error);
    }
  };

  const handleClassroomTestPublished = (wsData: any) => {
    try {
      // if (meetingRole === 'host') {
      //   console.log('classroom_test_published received by host – ignored');
      //   return;
      // }

      let bookId: string | null = null;
      let bookName: string | null = null;

      let envelope: any = wsData;
      if (typeof wsData === 'string') {
        try {
          envelope = JSON.parse(wsData);
        } catch (_) { }
      } else if (wsData?.type === 'Buffer' && Array.isArray(wsData.data)) {
        try {
          envelope = JSON.parse(wsData.data);
        } catch (_) { }
      }

      if (typeof envelope?.message === 'string') {
        const messageStr = envelope.message.replace(/^"|"$/g, '').trim();
        const bookIdMatch = messageStr.match(/book_id\s*:\s*([^,]+)/);
        const bookNameMatch = messageStr.match(/book_name\s*:\s*(.+)$/);
        bookId = bookIdMatch ? bookIdMatch[1].trim() : null;
        bookName = bookNameMatch ? bookNameMatch[1].trim() : null;
      }

      if (!bookId && envelope?.book_id) {
        bookId = String(envelope.book_id);
        bookName = envelope.book_name ?? null;
      }

      if (!bookId) {
        console.log('classroom_test_published: no book_id found in payload');
        return;
      }

      const cleanBookKey = String(bookId).replace(/[^0-9]/g, '');

      setTestParams({bookKey: cleanBookKey, name: bookName ?? ''});
      setTestBannerDismissed(false);

      const testUrl = `${window.location.origin}/resources?tab=user_assignment&name=${encodeURIComponent(bookName ?? '')}&bookKey=${cleanBookKey}`;
      console.log('Opening test in new tab for student:', testUrl);
      window.open(testUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error handling classroom_test_published:', error);
    }
  }

  const handleNotificationClose = () => {
    setNotificationState({
      data: null,
      isVisible: false,
    });
  };

  // const {mutate: endClassroomMeeting} = useClassroomEndMeetingApi();

  // Only run this effect when zoomMeetingInfo is properly initialized
  useEffect(() => {
    console.log('Zoom info from server in useEffect', zoomMeetingInfo);
    if (
      zoomMeetingInfo.signature &&
      zoomMeetingInfo.meetingId &&
      zoomMeetingInfo.tk &&
      zoomMeetingInfo.pwd
    ) {
      setLoading(false);
    }
  }, [zoomMeetingInfo]);

  // WebSocket setup - only run when we have the required data
  useEffect(() => {
    if (socket && zoomMeetingInfo.signature && source !== 'recording') {
      console.log('websocket present');
      socket.registerMessageType(
        'classroom_puzzle_created',
        schema.classroomCreatePuzzle,
      );
      socket.registerMessageType(
        'classroom_notification',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_manual_award',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_speed_flash',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_mystery_activated',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_end_celebration',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_auto_award',
        schema.classroomNotification,
      );
      socket.registerMessageType(
        'classroom_mini_tournament_created',
        schema.classroomCreatePuzzle,
      );
      socket.registerMessageType(
        'classroom_puzzle_royale_created',
        schema.classroomCreatePuzzle,
      );
      socket.registerMessageType(
        'classroom_puzzle_royale_ended',
        schema.classroomCreatePuzzle,
      );
      console.log('websocket messageType');
      // Handlers capture state from this render; re-registration count shows
      // how stale a push handler can be.
      trackPuzzle('classroom_ws_handlers_registered', {
        socket_id: (socket as any)?.id,
        has_meeting_id: !!zoomMeetingInfo.meetingId,
        has_tk: !!zoomMeetingInfo.tk,
        has_pwd: !!zoomMeetingInfo.pwd,
      });
      socket.registerMessageHandler(
        'classroom_puzzle_created',
        function (webSocket: any, wsData: any) {
          checkActivePuzzle(webSocket, wsData);
        },
      );
      socket.registerMessageHandler(
        'classroom_mini_tournament_created',
        function () {
          if (meetingId && occurenceId) {
            getActiveMiniTournament({meetingId, occurenceId});
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_puzzle_royale_created',
        function () {
          // The coach already refetched via registerClassroomRoyale's onSettled
          // (the reliable, response-synchronous read that confirms the freshly-
          // created row). Skipping the coach here drops the duplicate get_active
          // that used to fire on every create — students still need this WS push
          // to raise their Join banner.
          if (isCoach) return;
          if (meetingId && occurenceId) {
            getActiveClassroomRoyale({meetingId, occurenceId});
          }
        },
      );
      // Server pushes this the instant the royale's ended_at is stamped
      // (game-end). Re-fetch to clear the cached activeRoyale → the coach's
      // Create gate frees up immediately. This is what lets us drop the 10s
      // get_active poll entirely (previously the ONLY way the tab noticed a
      // royale had ended before its 15-min expiry).
      socket.registerMessageHandler(
        'classroom_puzzle_royale_ended',
        function () {
          if (meetingId && occurenceId) {
            getActiveClassroomRoyale({meetingId, occurenceId});
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_mini_tournament_game_result',
        function (_webSocket: any, wsData: any) {
          try {
            let parsed = wsData.message;
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (parsed?.result === 'Win' && !isCoach && meetingId && occurenceId) {
              reportGameWon({
                meetingId,
                occurenceId,
                tournamentGameId: parsed.game_id,
              });
              showOverlay('award_toast', {
                studentName: 'You',
                awardKey: 'tournament_game_win',
              });
            }
          } catch (e) {
            console.error('classroom_mini_tournament_game_result parse error:', e, wsData);
          }
          try {
            refreshLeaderboardSoon(queryClient);
            refreshRecognitionSoon(queryClient);
          } catch (e) {
            console.error('classroom_mini_tournament_game_result refreshSoon error:', e);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_notification',
        function (webSocket: any, wsData: any) {
          handleClassroomNotification(wsData);
        },
      );
      socket.registerMessageHandler(
        'classroom_manual_award',
        function (_webSocket: any, wsData: any) {
          try {
            let parsed = wsData.message;
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            console.log('classroom_manual_award received:', parsed);
            const awardType = parsed?.award_type || parsed?.awardType || '';
            const studentName = parsed?.student_name || parsed?.studentName || '';
            const awardedUserId = parsed?.student_user_id ?? parsed?.studentUserId;
            if (awardType) {
              showOverlay('award_toast', {studentName: studentName || 'Student', awardKey: awardType});
            }
            const awardedUidNum = awardedUserId != null ? Number(awardedUserId) : null;
            const myUidNum = userId != null ? Number(userId) : null;
            if (awardedUidNum != null) {
              if (!isCoach && myUidNum != null && awardedUidNum === myUidNum) {
                queryClient.setQueryData<any>(
                  ['studentRecognitionState', meetingId || '', occurenceId || ''],
                  (old: any) => {
                    const next = old ? {...old, stars: (old.stars ?? 0) + 1} : old;
                    if (next) prevStarsRef.current = next.stars;
                    return next;
                  },
                );
                fireStarFly(1);
              } else if (isCoach) {
                queryClient.setQueryData<any>(
                  ['coachRecognitionPanel', meetingId || '', occurenceId || ''],
                  (old: any) => {
                    if (!old?.students) return old;
                    return {
                      ...old,
                      students: old.students.map((s: any) => {
                        if (Number(s.user_id) !== awardedUidNum) return s;
                        const prevCounts = s.manual_award_counts || {};
                        return {
                          ...s,
                          stars: (s.stars ?? 0) + 1,
                          coach_awards: (s.coach_awards ?? 0) + 1,
                          manual_award_counts: awardType
                            ? {...prevCounts, [awardType]: (prevCounts[awardType] ?? 0) + 1}
                            : prevCounts,
                        };
                      }),
                    };
                  },
                );
              }
            }
          } catch (e) {
            console.error('classroom_manual_award parse error:', e, wsData);
          }
          try {
            refreshLeaderboardSoon(queryClient);
          } catch (e) {
            console.error('classroom_manual_award refreshSoon error:', e);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_speed_flash',
        function (_webSocket: any, wsData: any) {
          try {
            let parsed = wsData.message;
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            // The solver already saw this from their own verify response
            // (handleAutoStarsAwarded) — the class-wide broadcast includes them too.
            if (parsed?.user_id != null && String(parsed.user_id) === String(userIdRef.current)) return;
            const name = parsed?.student_name || 'Student';
            const t = parsed?.solve_time;
            showOverlay('speed_flash', {
              name,
              solveTime: t != null ? `${t}s` : '',
            });
          } catch (e) {
            console.error('classroom_speed_flash parse error:', e, wsData);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_mystery_activated',
        function (_webSocket: any, wsData: any) {
          try {
            let parsed = wsData.message;
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            console.log('classroom_mystery_activated received:', parsed);
            showOverlay('mystery', {});
          } catch (e) {
            console.error('classroom_mystery_activated parse error:', e, wsData);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_end_celebration',
        function (_webSocket: any, wsData: any) {
          try {
            console.log('classroom_end_celebration received:', wsData);
            setCelebrationTrigger(t => t + 1);
          } catch (e) {
            console.error('classroom_end_celebration handler error:', e, wsData);
          }
          try {
            refreshLeaderboardSoon(queryClient);
            refreshRecognitionSoon(queryClient);
          } catch (e) {
            console.error('classroom_end_celebration refreshSoon error:', e);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_auto_award',
        function (_webSocket: any, wsData: any) {
          try {
            let parsed = wsData.message;
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            console.log('classroom_auto_award received:', parsed);
            const eventType = parsed?.event_type || '';
            const studentName = parsed?.student_name || 'You';
            if (eventType) {
              showOverlay('award_toast', {studentName, awardKey: eventType});
            }
          } catch (e) {
            console.error('classroom_auto_award parse error:', e, wsData);
          }
          try {
            refreshRecognitionSoon(queryClient);
          } catch (e) {
            console.error('classroom_auto_award refreshSoon error:', e);
          }
        },
      );
      socket.registerMessageHandler(
        'classroom_test_published',
        function (_webSocket: any, wsData: any) {
          handleClassroomTestPublished(wsData);
        },
      );
      console.log('websocket messageHandler');
    }
  }, [socket, zoomMeetingInfo.signature, source]);

  useEffect(() => {
    console.log(
      'Current Active Puzzle from Server in useEffect',
      currentActivePuzzle,
    );
  }, [currentActivePuzzle]);

  // Pull active classroom mini-tournament on mount and whenever a join_tournament_response
  // arrives (SocketProvider mirrors data.tournamentId into joinedTournamentId).
  useEffect(() => {
    if (!meetingId || !occurenceId) return;
    getActiveMiniTournament({meetingId, occurenceId});
  }, [meetingId, occurenceId, joinedTournamentId, getActiveMiniTournament]);

  // Pull the latest active classroom puzzle royale ONCE on mount (covers a
  // student who joined late or refreshed, and a coach reloading the tab). Every
  // state transition after that is delivered by WS pushes — the coach's own
  // create refetches via registerClassroomRoyale's onSettled; the
  // `classroom_puzzle_royale_created` / `classroom_puzzle_royale_ended` handlers
  // cover create/end for everyone else. So this endpoint is now fully
  // event-driven: no 10s poll (the old source of the ~131k redundant calls) and
  // no focus/visibilitychange refetch (that fired on every tab switch — in
  // pairs — and became the dominant caller once the poll was gone).
  useEffect(() => {
    if (!meetingId || !occurenceId) return;
    getActiveClassroomRoyale({meetingId, occurenceId});
  }, [meetingId, occurenceId, getActiveClassroomRoyale]);

  // A different tournament becoming active resets the per-tournament join flag.
  useEffect(() => {
    setHasJoinedActiveTournament(false);
  }, [activeMiniTournament?.tournament_id]);

  // Get classroom info - only run when we have the required data
  useEffect(() => {
    if (
      dataLoaded &&
      webinarData &&
      !zoomMeetingInfo.signature &&
      zoomMeetingInfo.meetingId &&
      zoomMeetingInfo.tk &&
      zoomMeetingInfo.pwd &&
      userId
    ) {
      console.log('Getting classroom info with:', {
        meetingId: zoomMeetingInfo.meetingId,
        userId,
        occurenceId,
        meetingRole,
        batchType,
      });
      getClassroomInfo({
        meetingId: zoomMeetingInfo.meetingId,
        userId,
        occurenceId,
        meetingRole,
        batchType,
      });
    }
  }, [
    dataLoaded,
    webinarData,
    zoomMeetingInfo.signature,
    zoomMeetingInfo.meetingId,
    zoomMeetingInfo.tk,
    zoomMeetingInfo.pwd,
    userId,
    occurenceId,
    meetingRole,
    batchType,
  ]);

  // Get active puzzle - only run when we have the required data
  useEffect(() => {
    if (
      dataLoaded &&
      webinarData &&
      zoomMeetingInfo.meetingId &&
      occurenceId &&
      zoomMeetingInfo.signature &&
      source !== 'recording'
    ) {
      console.log('Getting active puzzle with:', {
        meetingId: zoomMeetingInfo.meetingId,
        occurenceId,
      });
      getActivePuzzle({
        meetingId: zoomMeetingInfo.meetingId,
        occurenceId,
        trigger: 'mount',
      });
    }
  }, [
    dataLoaded,
    webinarData,
    zoomMeetingInfo.meetingId,
    occurenceId,
    zoomMeetingInfo.signature,
    source,
  ]);

  useEffect(() => {
    if (tabsLeft[activeTabLeft].label === 'Past Puzzles') {
      setPastPuzzleId(prev => prev + 1);
    }
  }, [activeTabLeft]);

  //Local Change
  useEffect(() => {
    return () => {
      localStorage.removeItem('ZoomUserData');
      timerStartedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isCoach) return;
    const handleCoachWrapUp = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'cc_meeting_ended') return;
      if (!meetingId || !occurenceId) return;
      if (wrapUpVisible) return;
      computeEndCelebration({ meetingId, occurenceId });
    };
    window.addEventListener('message', handleCoachWrapUp);
    return () => window.removeEventListener('message', handleCoachWrapUp);
  }, [isCoach, meetingId, occurenceId, wrapUpVisible, computeEndCelebration]);

  useEffect(() => {
    const handleMeetingEndedFeedback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data;
      if (data?.type !== 'cc_meeting_ended') {
        return;
      }
      if (meetingRole !== 'student') {
        return;
      }

      const payload = data?.payload || {};
      const payloadClassType = String(payload?.classType || '').toLowerCase();

      const resolvedWebinarId = Number(payload?.webinarRowId || 0);
      const resolvedStudentId = Number(payload?.studentId || userId || 0);
      if (!resolvedWebinarId || !resolvedStudentId) {
        return;
      }

      const resolvedTopic = payload?.topic || topic || 'Demo Class';
      const parsedCoachName =
        typeof resolvedTopic === 'string' && resolvedTopic.includes('|')
          ? (resolvedTopic.split('|')?.[1] || '').split('-')?.[0]?.trim()
          : 'Coach';

      setClassReviewMeta({
        webinarId: resolvedWebinarId,
        studentId: resolvedStudentId,
        topic: resolvedTopic,
        coachName: parsedCoachName || 'Coach',
        classType: payloadClassType || 'default',
      });
      setShowClassReviewModal(true);
    };

    window.addEventListener('message', handleMeetingEndedFeedback);
    return () => {
      window.removeEventListener('message', handleMeetingEndedFeedback);
    };
  }, [topic, userId]);

  return (
    <View style={styles.root}>
      {meetingRole !== 'host' &&
        activeMiniTournament &&
        new Date(activeMiniTournament.end_date).getTime() > Date.now() && (
          <MiniTournamentBanner
            name={activeMiniTournament.name}
            startDateIso={activeMiniTournament.start_date}
            onOpen={handleStudentOpenTournament}
          />
        )}
      {meetingRole !== 'host' &&
        activeRoyale &&
        !royaleBannerDismissed &&
        new Date(activeRoyale.expires_at).getTime() > Date.now() && (
          <PuzzleRoyaleBanner
            joining={isJoiningRoyale}
            onJoin={handleStudentJoinRoyale}
            onClose={() => setRoyaleBannerDismissed(true)}
          />
        )}
      {meetingRole !== 'host' && testParams?.bookKey && !testBannerDismissed && (
        <ClassroomTestBanner
          name={testParams?.name || 'Test'}
          onOpen={handleStudentOpenTest}
          onClose={() => setTestBannerDismissed(true)}
        />
      )}
      <RoyaleCreateModal
        visible={isCreateRoyaleModalOpen}
        onClose={() => setIsCreateRoyaleModalOpen(false)}
        onCreate={handleCoachCreateRoyale}
        batchType={batchType}
      />
      <MiniTournamentCreateModal
        visible={isCreateMiniModalOpen}
        isSubmitting={isCreatingMiniTournament}
        onClose={() => setIsCreateMiniModalOpen(false)}
        onSubmit={({timeControl, rounds}) =>
          createMiniTournament({
            meetingId,
            occurenceId,
            batchType,
            timeControl,
            rounds,
          })
        }
      />
      <MiniTournamentStartConfirmModal
        visible={oddPlayerModalVisible}
        isSubmitting={isStartingNow}
        onJoinAndStart={() => triggerStartNow(true)}
        onStartWithout={() => triggerStartNow(false)}
        onClose={() => setOddPlayerModalVisible(false)}
      />
      {showNudgePopup && (
        <SlideModal
          visible={showNudgePopup}
          contentPadding={0}
          type="center"
          customStyle={styles.reason}>
          <Box
            flexDirection="column"
            gap={10}
            justifyContent="flex-start"
            alignItems="center">
            <Box
              justifyContent="center"
              alignItems="center"
              style={[
                styles.modalIcon,
                {
                  backgroundColor: icon?.bgColor,
                },
              ]}>
              <MaterialCommunityIcons
                size={30}
                name={icon?.name}
                color={icon?.iconColor}
              />
            </Box>
            <CCText
              style={{
                marginVertical: 10,
              }}
              fontSize={FontSizes[16]}
              fontWeight={400}>
              {nudgeMessage}
            </CCText>
            <CCButton
              contentContainerStyle={{
                paddingVertical: 5,
              }}
              onPress={() => setShowNudgePopup(false)}>
              <CCText fontSize={FontSizes[14]} fontWeight={500}>
                Okay
              </CCText>
            </CCButton>
          </Box>
        </SlideModal>
      )}
      <Loader loading={loading} textToShow={'Loading Your Classroom'} />
      <ClassReviewModal
        visible={showClassReviewModal}
        onClose={() => setShowClassReviewModal(false)}
        topic={classReviewMeta.topic}
        coachName={classReviewMeta.coachName}
        webinarId={classReviewMeta.webinarId}
        classType={classReviewMeta.classType}
        userData={{user_id: classReviewMeta.studentId}}
        onSubmit={() => setShowClassReviewModal(false)}
      />
      <ClassroomNotification
        notificationData={notificationState.data}
        handleNotificationClose={handleNotificationClose}
        showPopUp={notificationState.isVisible}
      />
      {overlayState?.kind && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            pointerEvents: overlayState.kind === 'celebration' || (overlayState.kind === 'streak' && (overlayState.props?.streak ?? 0) >= 7) ? 'auto' : 'none',
          }}>
          <OverlayManager key={overlayKey} overlay={overlayState} onClear={clearOverlay} role={isCoach ? 'coach' : 'student'} />
        </div>,
        document.body,
      )}
      {starFlyState && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 2147483646,
            pointerEvents: 'none',
          }}>
          <StarFlyHtml key={starFlyState.key} count={starFlyState.count} onDone={clearStarFly} />
        </div>,
        document.body,
      )}
      <HelpModal
        sections={CLASSROOM_HELP_SECTIONS}
        directLink={
          joinUrl
            ? {label: 'Zoom Link', url: joinUrl}
            : undefined
        }
      />
      <SlideModal visible={showPopup} type="center" customStyle={styles.err}>
        <PopUp
          confirm={() => setShowPopup(false)}
          text={message}
          label={holdLabel || undefined}
          buttonConfirm="Okay"
        />
      </SlideModal>
      <Divider width={1} />
      {meetingId && occurenceId && (
        <View
          onLayout={(e: {nativeEvent: {layout: {height: number}}}) => {
            const h = e.nativeEvent.layout.height;
            if (h && Math.abs(h - recognitionStripHeight) > 1) {
              setRecognitionStripHeight(h);
            }
          }}>
          {isCoach ? (
            <>
            <View style={{flexDirection: 'row', alignItems: 'stretch'}}>
              <View style={{flex: 1}}>
                <ThinCoachStrip
                  recognisedCount={coachPanelData?.coverage?.recognised_count ?? 0}
                  totalCount={coachPanelData?.coverage?.total_count ?? 0}
                  manualCount={manualAwardCount}
                  mysteryCount={coachPanelData?.mysteryCount ?? 0}
                />
              </View>
              <TouchableOpacity
                onPress={handleOpenWrapUp}
                style={{
                  paddingHorizontal: 14,
                  justifyContent: 'center',
                  backgroundColor: '#FACF47',
                  borderLeftWidth: 1,
                  borderLeftColor: 'rgba(38,35,34,0.12)',
                }}>
                <CCText style={{fontSize: 12, fontWeight: '900', color: '#4D3F37', letterSpacing: 0.5}}>
                  🏆 Wrap Up
                </CCText>
              </TouchableOpacity>
            </View>
            {/* Zones moved off the top bar -> shown as a per-student dot in the live list
                (zoneByUserId in the Leaderboard tab). zonePanelData still drives those dots. */}
            </>
          ) : isMobile ? (
            <StudentMiniBanner
              stars={studentRecognitionState?.stars ?? 0}
              streak={studentRecognitionState?.streak?.current ?? 0}
              awardsCount={studentRecognitionState?.awards?.length ?? 0}
              onTap={() => setStarsScreenVisible(true)}
            />
          ) : (
            <ThinStudentStrip
              stars={studentRecognitionState?.stars ?? 0}
              streak={studentRecognitionState?.streak?.current ?? 0}
              awards={studentRecognitionState?.awards ?? []}
            />
          )}
        </View>
      )}
      {!isCoach && isMobile && starsScreenVisible && (
        <Modal visible transparent animationType="slide" onRequestClose={() => setStarsScreenVisible(false)}>
          <View style={{flex: 1, backgroundColor: '#FAF6EB'}}>
            <View style={{flexDirection: 'row', alignItems: 'center', padding: 12, backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: 'rgba(38,35,34,0.12)'}}>
              <TouchableOpacity onPress={() => setStarsScreenVisible(false)} style={{padding: 6}}>
                <Text style={{fontSize: 18, color: '#4D3F37'}}>✕</Text>
              </TouchableOpacity>
              <Text style={{flex: 1, textAlign: 'center', fontSize: 16, fontWeight: '800', color: '#4D3F37'}}>My Stars</Text>
              <View style={{width: 30}} />
            </View>
            <StudentStarsScreen
              stars={studentRecognitionState?.stars ?? 0}
              streak={studentRecognitionState?.streak?.current ?? 0}
              awards={studentRecognitionState?.awards ?? []}
              events={studentStarEventsData?.events ?? []}
              rank={1}
              totalStudents={1}
              studentName={userName || 'You'}
            />
          </View>
        </Modal>
      )}
      <EndOfClassConfirmModal
        visible={wrapUpVisible}
        initialCelebration={computedCelebration}
        classStudents={coachPanelData?.students ?? []}
        hostUserId={userId ? Number(userId) : undefined}
        autoPublishSeconds={60}
        onPublish={handlePublishCelebration}
        onClose={() => setWrapUpVisible(false)}
      />

      <View style={styles.container}>
        <Divider width={1} isVertical={true} />
        {/* Container 1 - Left 70% */}
        <View style={styles.leftTabsContainer}>
          <View style={styles.tab}>
            <Box
              flexDirection="row"
              alignItems="center"
              style={{
                width: '100%',
              }}>
              <Box flexDirection="row" flex={1}>
                {tabsLeft.map((tab, tabIndex) => (
                  <TouchableOpacity
                    key={tab.label}
                    onPress={() => setActiveTabLeft(tabIndex)}>
                    <View
                      style={[
                        styles.tab,
                        activeTabLeft === tabIndex && styles.activeTab,
                      ]}>
                      <Icon
                        name={tab.icon}
                        size={20}
                        color={
                          activeTabLeft === tabIndex
                            ? CCColors.TextColor.Primary
                            : CCColors.TextColor.LightGrey
                        }
                      />
                      <CCText
                        style={[
                          styles.leftTabTitle,
                          activeTabLeft === tabIndex && styles.activeTab,
                        ]}
                        fontSize={14}>
                        {tab.label}
                      </CCText>
                    </View>
                    <View
                      style={[
                        activeTabLeft === tabIndex
                          ? styles.tabUnderline
                          : {marginTop: 10},
                      ]}
                    />
                  </TouchableOpacity>
                ))}
              </Box>
              {showTimer !== null && isRunning && countdownExpiry && (
                <ClassEndCountdown
                  expiryTimestamp={countdownExpiry}
                  onExpire={handleCountdownExpire}
                  onLowTime={handleCountdownLowTime}
                  boxStyle={styles.timer}
                  textStyle={styles.timerText}
                  fontSize={RFPercentage(1.8)}
                />
              )}
              {isZoomMeeting === 1 && !isRunning && (
                <IconTooltipButton
                  icon="hand-wave-outline"
                  tooltip={`Nudge ${
                    meetingRole === 'host' ? 'Student' : 'Coach'
                  }`}
                  disabled={disable}
                  onPress={() => {
                    if (!disable) {
                      nudge({
                        webinar_id: meetingId,
                        occurence_id: occurenceId,
                        meeting_role: meetingRole,
                        class_type: batchType,
                      });
                      setDisable(true);
                    }
                  }}
                  iconColor={
                    disable ? CCColors.MeetingStatus.Grey : CCColors.White
                  }
                  style={[
                    styles.iconActionBtn,
                    {
                      backgroundColor: disable
                        ? CCColors.OrangeDisabled
                        : CCColors.Orange,
                      marginLeft: 'auto',
                    },
                  ]}
                />
              )}
              {/* TEMPORARY: gate mini-tournament host UI to user 31609 while feature is in beta. Remove gate when releasing widely. */}
              {meetingRole === 'host' && !activeMiniTournament && (
                <IconTooltipButton
                  icon="trophy-outline"
                  tooltip={
                    isCreatingMiniTournament
                      ? 'Starting…'
                      : 'Create tournament'
                  }
                  disabled={isCreatingMiniTournament}
                  onPress={() => {
                    if (isCreatingMiniTournament) return;
                    setIsCreateMiniModalOpen(true);
                  }}
                  iconColor={
                    isCreatingMiniTournament
                      ? CCColors.MeetingStatus.Grey
                      : CCColors.White
                  }
                  style={[
                    styles.iconActionBtn,
                    {
                      backgroundColor: isCreatingMiniTournament
                        ? CCColors.OrangeDisabled
                        : CCColors.Orange,
                    },
                  ]}
                />
              )}
              {/* Puzzle Royale: only in a live zoom/webinar classroom (like Nudge
                  & tournaments — gated on isZoomMeeting). Single flip button:
                  no active royale → "Create Puzzle Royale" (instant create in
                  THIS tab + notify students); active → "Open Puzzle Royale"
                  (rejoin as host in a new tab). Reverts to Create after the
                  game ends (server stamps ended_at). */}
              {isZoomMeeting === 1 && meetingRole === 'host' && (
                <IconTooltipButton
                  // Distinct icon AND fill style per state so the coach can't
                  // confuse Create vs Open at a glance:
                  //   • CREATE  → FILLED teal (primary action — make a new race)
                  //   • OPEN    → OUTLINE teal (secondary — a race already
                  //               exists, just reopen it as host)
                  //   • CREATING→ disabled filled (in flight)
                  icon={
                    creatingRoyale
                      ? 'puzzle-outline'
                      : hasActiveRoyale
                      ? 'puzzle-check-outline'
                      : 'puzzle-plus-outline'
                  }
                  tooltip={
                    creatingRoyale
                      ? 'Creating Puzzle Royale…'
                      : hasActiveRoyale
                      ? 'Open Puzzle Royale'
                      : 'Create Puzzle Royale'
                  }
                  disabled={creatingRoyale}
                  onPress={handleCoachCreateRoyalePressed}
                  // Open (outline) uses the teal as the icon/border colour;
                  // Create/Creating (filled) use white on the teal/orange fill.
                  iconColor={
                    !creatingRoyale && hasActiveRoyale ? '#3DAB9E' : CCColors.White
                  }
                  style={[
                    styles.iconActionBtn,
                    creatingRoyale
                      ? {backgroundColor: CCColors.OrangeDisabled}
                      : hasActiveRoyale
                      ? {
                          // OUTLINE: transparent fill + teal border.
                          backgroundColor: 'transparent',
                          borderWidth: 1.5,
                          borderColor: '#3DAB9E',
                        }
                      : {backgroundColor: '#3DAB9E'},
                  ]}
                />
              )}
              {meetingRole === 'host' && activeMiniTournament && (
                <Box
                  flexDirection="row"
                  gap={6}
                  alignItems="center"
                  style={{marginLeft: 6}}>
                  {new Date(activeMiniTournament.start_date).getTime() -
                    Date.now() >
                    20000 && (
                    <IconTooltipButton
                      icon="play-circle-outline"
                      tooltip={
                        isStartingNow || isFetchingPlayerCount
                          ? 'Starting…'
                          : 'Start now'
                      }
                      disabled={isStartingNow || isFetchingPlayerCount}
                      onPress={handleCoachStartNow}
                      iconColor={CCColors.White}
                      style={[
                        styles.iconActionBtn,
                        {
                          marginLeft: 0,
                          backgroundColor:
                            isStartingNow || isFetchingPlayerCount
                              ? CCColors.OrangeDisabled
                              : CCColors.Orange,
                        },
                      ]}
                    />
                  )}
                  <IconTooltipButton
                    icon="open-in-new"
                    tooltip="Open tournament"
                    onPress={() =>
                      openTournamentInNewTab(
                        activeMiniTournament.tournament_id,
                        activeMiniTournament.name,
                      )
                    }
                    iconColor={CCColors.Orange}
                    style={[
                      styles.iconActionBtn,
                      {
                        marginLeft: 0,
                        backgroundColor: CCColors.White,
                        borderWidth: 1,
                        borderColor: CCColors.Orange,
                      },
                    ]}
                  />
                </Box>
              )}
            </Box>
          </View>
          <Divider width={1} />
          {/* Tab Content */}
          <View
            style={[styles.leftTabContentContainer, {flex: 1, minHeight: 0}]}>
            {tabsLeft.map((tab, tabIndex) => {
              const {component: LeftComponent, getProps} = tab;
              // Coach Screen (tabIndex 0) hosts the cross-origin Zoom iframe.
              // Chromium throttles cross-origin iframes that are display:none
              // OR positioned outside the viewport: requestAnimationFrame and
              // ResizeObserver pause. The Zoom Web SDK drives its WebGL video
              // loop with rAF, so throttling makes student video come back
              // black on tab switch (Edge especially). Keep the iframe full
              // size and in-viewport, just layered behind the active tab.
              // Override the base style's opacity:0 so the iframe actually
              // composes pixels; the opaque active sibling occludes it.
              const isCoachScreen = tabIndex === 0;
              const inactiveStyle = isCoachScreen
                ? ({
                    // position:absolute so it stacks behind the active tab
                    // instead of taking layout flow space. Still full size
                    // and in-viewport, so cross-origin iframe throttling
                    // does NOT kick in and the Zoom rAF loop keeps running.
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 1,
                    pointerEvents: 'none',
                    zIndex: 0,
                    // isolate so the Floating Window pill (zIndex:1000 inside
                    // IframeZoom) can't escape this wrapper's stacking context
                    // and punch above the active tab.
                    isolation: 'isolate',
                  } as const)
                : ({display: 'none'} as const);
              return (
                <View
                  key={tab.uniqueKey ?? tabIndex}
                  style={[
                    styles.leftTabContent,
                    activeTabLeft === tabIndex && styles.leftTabActiveContent,
                    activeTabLeft !== tabIndex && inactiveStyle,
                  ]}>
                  {zoomMeetingInfo.signature !== '' || source == 'recording' ? (
                    <LeftComponent {...(getProps ? getProps() : {})} />
                  ) : (
                    <CCText>Loading Your Class</CCText>
                  )}
                </View>
              );
            })}
          </View>
        </View>
        {meetingRole !== 'mentor' && (
          <>
            <Divider width={1} isVertical={true} />
            {/* Container 2 - Right 30% */}
            <View style={styles.rightTabsContainer}>
              <View style={styles.tab}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {tabsRight.map((tab, tabIndex) => (
                    <TouchableOpacity
                      key={tab.label}
                      onPress={() => setActiveTabRight(tabIndex)}>
                      <View
                        style={[
                          styles.tab,
                          activeTabRight === tabIndex && styles.activeTab,
                        ]}>
                        <Icon
                          name={tab.icon}
                          size={20}
                          color={
                            activeTabRight === tabIndex
                              ? CCColors.TextColor.Primary
                              : CCColors.TextColor.LightGrey
                          }
                        />
                        <CCText
                          style={[
                            styles.rightTabTitle,
                            activeTabRight === tabIndex && styles.activeTab,
                          ]}
                          fontSize={14}>
                          {tab.label}
                        </CCText>
                      </View>
                      <View
                        style={[
                          activeTabRight === tabIndex
                            ? styles.tabUnderline
                            : {marginTop: 10},
                        ]}
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
              <Divider width={1} />
              {/* Tab Content */}
              <View
                style={[
                  styles.rightTabContentContainer,
                  {flex: 1, minHeight: 0},
                ]}>
                {tabsRight.map((tab, tabIndex) => {
                  const {component: RightComponent, getProps} = tab;
                  return (
                    <View
                      key={tab.uniqueKey || tabIndex}
                      style={[
                        styles.rightTabContent,
                        activeTabRight !== tabIndex && {display: 'none'},
                      ]}>
                      {activeTabRight === tabIndex && (
                        <RightComponent {...(getProps ? getProps() : {})} />
                      )}
                    </View>
                  );
                })}
              </View>
            </View>
          </>
        )}
      </View>
    </View>
  );
};

export default Classroom;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'column',
    // Fill the navigator scene (viewport minus the global Header above us).
    // NOT 100vh: 100vh ignores the Header, so the screen overflows its
    // scroll-parent by the Header's height, which scrolls the pinned
    // recognition strip out of view. overflow:hidden keeps the strip fixed
    // and pushes scrolling down into the inner panels.
    minHeight: 0,
    overflow: 'hidden',
  },
  container: {
    flexDirection: 'row',
    flex: 1,
    minHeight: 0,
    backgroundColor: CCColors.Primary.lightBrown,
  },
  leftTabsContainer: {
    // flexDirection: 'column',
    flex: 7,
    justifyContent: 'flex-start',
    minHeight: 0,
  },
  rightTabsContainer: {
    flex: 3,
    justifyContent: 'flex-start',
    minHeight: 0,
  },
  tab: {
    paddingHorizontal: 10,
    padding: 10,
    paddingTop: 7,
    paddingBottom: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    backgroundColor: CCColors.White,
  },
  activeTab: {
    fontWeight: 'bold',
  },
  tabUnderline: {
    marginTop: 7,
    height: 2,
    backgroundColor: CCColors.Primary.Default,
  },
  leftTabTitle: {
    marginLeft: 8,
    marginRight: 20,
    // marginBottom: 10,
    color: CCColors.TextColor.Primary,
  },
  rightTabTitle: {
    marginLeft: 8,
    marginRight: 8,
    // marginBottom: 10,
    color: CCColors.TextColor.Primary,
  },
  leftTabContentContainer: {
    position: 'relative',
    // height: '70%',
    backgroundColor: CCColors.Primary.lightBrown,
    //backgroundColor: 'blue',
  },
  leftTabContent: {
    paddingHorizontal: 15,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    opacity: 0,
    pointerEvent: 'none',
  },
  leftTabActiveContent: {
    zIndex: 2,
    opacity: 1,
    pointerEvents: 'auto',
    // Opaque background so the Coach Screen iframe sitting at zIndex:0
    // (kept in-viewport to avoid cross-origin iframe throttling) doesn't
    // bleed through gaps in the active tab's content.
    backgroundColor: CCColors.Primary.lightBrown,
  },
  rightTabContentContainer: {
    // height: '70%',
    backgroundColor: CCColors.White,
  },
  rightTabContent: {
    flex: 1,
    paddingHorizontal: 8,
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    paddingTop: 20,
  },
  err: {
    borderRadius: 12,
    backgroundColor: 'white',
    zIndex: 5,
    overflowY: 'hidden',
    minWidth: '20%',
    maxWidth: 480,
    width: '90%',
    margin: 'auto',
  },
  timer: {
    height: '90%',
    marginBottom: 2,
    backgroundColor: CCColors.Red,
    alignItems: 'center',
    borderRadius: 6,
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  timerText: {
    color: CCColors.White,
    textAlign: 'center',
    fontFamily: 'Helvetica Neue Bold',
  },
  wave: {
    padding: 9,
    borderRadius: 12,
    alignSelf: 'center',
    marginLeft: 'auto',
    marginBottom: 4,
  },
  // Icon-only classroom action button (label moved to a hover tooltip).
  iconActionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignSelf: 'center',
    marginBottom: 4,
    marginLeft: 6,
    cursor: 'pointer',
  },
  tabsnudge: {
    width: '100%',
  },
  reason: {
    borderRadius: 12,
    backgroundColor: 'white',
    zIndex: 5,
    overflowY: 'hidden',
    minWidth: '20%',
    paddingHorizontal: 25,
    paddingBottom: 25,
  },
  modalIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
  },
});
